import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { PencilLine, Save, UserRoundPlus, UsersRound, X } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { Button } from "../../shared/ui/Button";
import { Card, EmptyState, StatCard } from "../../shared/ui/Card";
import { PageSkeleton, StatusBadge } from "../../shared/ui/Status";
import { useOrganization, useOrganizationActions } from "./useOrganization";
import { SalesWorkbench } from "../sales/SalesWorkbench";
import { MemberCourseInquiryPanel } from "../sales/MemberCourseInquiryPanel";
import { env } from "../../shared/config/env";
import { MemberCoachAssignmentPanel } from "./MemberCoachAssignmentPanel";

const memberSchema = z.object({
  name: z.string().trim().min(1, "请输入会员姓名"),
  phone: z.string().regex(/^1\d{10}$/, "请输入正确的11位手机号"),
  gender: z.enum(["male", "female", "undisclosed"]),
  birthday: z.string().min(1, "请选择生日"),
  coachId: z.string().uuid("请选择主教练"),
});
type MemberForm = z.infer<typeof memberSchema>;

const memberUpdateSchema = z.object({
  name: z.string().trim().min(1, "请输入会员姓名").max(80, "姓名最多80个字符"),
  gender: z.enum(["male", "female", "undisclosed"]),
  birthday: z
    .string()
    .refine(
      (value) => !value || value <= new Date().toISOString().slice(0, 10),
      "生日不能晚于今天",
    ),
});
type MemberUpdateForm = z.infer<typeof memberUpdateSchema>;

export function MemberDirectoryPage({
  managerView = false,
}: {
  managerView?: boolean;
}) {
  const { session } = useAuth();
  const organization = useOrganization();
  const actions = useOrganizationActions();
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const form = useForm<MemberForm>({
    resolver: zodResolver(memberSchema),
    defaultValues: {
      name: "",
      phone: "",
      gender: "undisclosed",
      birthday: "",
      coachId:
        session?.profile.role === "manager" ? session.profile.id : "",
    },
  });
  const editForm = useForm<MemberUpdateForm>({
    resolver: zodResolver(memberUpdateSchema),
    defaultValues: { name: "", gender: "undisclosed", birthday: "" },
  });
  if (!session || !organization.data) return <PageSkeleton />;
  const members = organization.data.accounts.filter(
    (item) =>
      item.role === "member" && item.storeId === session.profile.storeId,
  );
  const coaches = organization.data.accounts.filter(
    (item) =>
      (item.role === "employee" || item.role === "manager") &&
      item.status === "active" &&
      item.storeId === session.profile.storeId,
  ).sort((left, right) =>
    left.id === session.profile.id
      ? -1
      : right.id === session.profile.id
        ? 1
        : left.name.localeCompare(right.name, "zh-CN"),
  );
  const birthdayCount = members.filter((member) =>
    hasBirthdayWithin(member.birthday, 30),
  ).length;
  const submit = async (input: MemberForm) => {
    setError("");
    setNotice("");
    try {
      await actions.createAccount({
        ...input,
        role: "member",
        storeId: session.profile.storeId!,
        jobTitle: "正式会员",
      });
      setNotice("会员档案已创建，登录账号已同步启用。");
      form.reset();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "创建失败");
    }
  };
  const startEditing = (member: (typeof members)[number]) => {
    setNotice("");
    setError("");
    setEditingMemberId(member.id);
    editForm.reset({
      name: member.name,
      gender: member.gender ?? "undisclosed",
      birthday: member.birthday ?? "",
    });
  };
  const submitMemberUpdate = async (input: MemberUpdateForm) => {
    if (!editingMemberId) return;
    setNotice("");
    setError("");
    try {
      await actions.updateMember({
        memberId: editingMemberId,
        name: input.name,
        gender: input.gender,
        birthday: input.birthday || null,
      });
      setEditingMemberId(null);
      setNotice("会员资料已保存，并已同步至其他角色端口。");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "会员资料保存失败");
    }
  };
  return (
    <div className="page-stack organization-page">
      <header className="page-heading">
        <div>
          <span>{managerView ? "本店会员范围" : "员工端前台职能"}</span>
          <h1>会员</h1>
          <p>只有购买课程的正式会员建档；一个手机号只能绑定一个身份。</p>
        </div>
        <StatusBadge tone="success">{session.profile.storeName}</StatusBadge>
      </header>
      {(notice || error) && (
        <div
          className={
            error ? "inline-notice inline-notice--error" : "inline-notice"
          }
        >
          {error || notice}
        </div>
      )}
      <div className="stat-grid">
        <StatCard
          label="本店会员"
          value={`${members.length}`}
          detail={
            env.VITE_AUTH_MODE === "supabase" ? "远程真实档案" : "本地演示档案"
          }
        />
        <StatCard
          label="启用账号"
          value={`${members.filter((m) => m.status === "active").length}`}
          detail="停用档案历史仍保留"
        />
        <StatCard
          label="即将到期"
          value={`${members.filter((member) => member.memberStatus === "expiring").length}`}
          detail="≤3课时或≤10天"
        />
        <StatCard
          label="生日提醒"
          value={`${birthdayCount}`}
          detail="未来30天"
        />
      </div>
      <div className="management-grid">
        <Card className="management-card">
          <div className="card-heading">
            <UserRoundPlus />
            <div>
              <span>{managerView ? "店长可建档" : "所有员工拥有前台职能"}</span>
              <h2>新增正式会员</h2>
            </div>
          </div>
          <form className="v4-form" onSubmit={form.handleSubmit(submit)}>
            <label className="v4-field">
              <span>会员姓名</span>
              <input {...form.register("name")} placeholder="请输入姓名" />
              {form.formState.errors.name && (
                <small>{form.formState.errors.name.message}</small>
              )}
            </label>
            <label className="v4-field">
              <span>手机号</span>
              <input
                {...form.register("phone")}
                inputMode="numeric"
                placeholder="11位手机号"
              />
              {form.formState.errors.phone && (
                <small>{form.formState.errors.phone.message}</small>
              )}
            </label>
            <div className="form-split">
              <label className="v4-field">
                <span>性别</span>
                <select {...form.register("gender")}>
                  <option value="male">男</option>
                  <option value="female">女</option>
                  <option value="undisclosed">不公开</option>
                </select>
              </label>
              <label className="v4-field">
                <span>生日</span>
                <input type="date" {...form.register("birthday")} />
                {form.formState.errors.birthday && (
                  <small>{form.formState.errors.birthday.message}</small>
                )}
              </label>
            </div>
            <label className="v4-field">
              <span>主教练</span>
              <select {...form.register("coachId")}>
                <option value="">请选择</option>
                {coaches.map((coach) => (
                  <option key={coach.id} value={coach.id}>
                    {coach.name}
                  </option>
                ))}
              </select>
              {form.formState.errors.coachId && (
                <small>{form.formState.errors.coachId.message}</small>
              )}
            </label>
            <Button disabled={actions.pending || !coaches.length}>
              <UserRoundPlus size={16} />
              创建会员
            </Button>
          </form>
        </Card>
        <Card className="management-card member-rule-card">
          <UsersRound />
          <h2>建档边界</h2>
          <p>
            员工和店长只能在自己的门店创建会员；姓名、手机号、性别、生日、所属门店和主教练构成正式会员档案。未知手机号不能直接登录。
          </p>
        </Card>
      </div>
      <div className="section-divider">
        <span>建客后购课闭环</span>
        <h2>订单与收款</h2>
      </div>
      <SalesWorkbench />
      {managerView && (
        <>
          <div className="section-divider">
            <span>多人协同授课</span>
            <h2>会员授课团队</h2>
          </div>
          <MemberCoachAssignmentPanel storeId={session.profile.storeId} />
        </>
      )}
      <Card className="directory-card">
        <div className="card-heading">
          <UsersRound />
          <div>
            <span>仅当前门店</span>
            <h2>会员目录</h2>
          </div>
        </div>
        {members.length ? (
          <div className="member-list">
            {members.map((member) => (
              <div className="member-list-item" key={member.id}>
                <div className="member-list-summary">
                  <span className="member-avatar">{member.name.slice(0, 1)}</span>
                  <div className="member-list-copy">
                    <strong>{member.name}</strong>
                    <small>
                      {member.phone} · {member.birthday || "生日未录入"} · {genderLabel(member.gender)}
                    </small>
                  </div>
                  <div className="member-list-actions">
                    <StatusBadge
                      tone={
                        member.memberStatus === "active"
                          ? "success"
                          : member.memberStatus === "expiring"
                            ? "warning"
                            : member.memberStatus === "frozen"
                              ? "danger"
                              : "neutral"
                      }
                    >
                      {member.status !== "active"
                        ? "账号未启用"
                        : memberStatusLabel(member.memberStatus)}
                    </StatusBadge>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => startEditing(member)}
                    >
                      <PencilLine size={14} />
                      编辑资料
                    </Button>
                  </div>
                </div>
                {editingMemberId === member.id && (
                  <form
                    className="member-edit-form"
                    onSubmit={editForm.handleSubmit(submitMemberUpdate)}
                  >
                    <label className="v4-field">
                      <span>会员姓名</span>
                      <input {...editForm.register("name")} />
                      {editForm.formState.errors.name && (
                        <small>{editForm.formState.errors.name.message}</small>
                      )}
                    </label>
                    <label className="v4-field">
                      <span>性别</span>
                      <select {...editForm.register("gender")}>
                        <option value="male">男</option>
                        <option value="female">女</option>
                        <option value="undisclosed">不公开</option>
                      </select>
                    </label>
                    <label className="v4-field">
                      <span>生日（可后续补录）</span>
                      <input
                        type="date"
                        max={new Date().toISOString().slice(0, 10)}
                        {...editForm.register("birthday")}
                      />
                      {editForm.formState.errors.birthday && (
                        <small>{editForm.formState.errors.birthday.message}</small>
                      )}
                    </label>
                    <label className="v4-field">
                      <span>登录手机号（只读）</span>
                      <input value={member.phone} disabled />
                    </label>
                    <div className="member-edit-actions">
                      <Button disabled={actions.pending}>
                        <Save size={14} />
                        保存资料
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setEditingMemberId(null)}
                      >
                        <X size={14} />
                        取消
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            title="暂无会员"
            description="使用上方表单创建本店第一位正式会员。"
          />
        )}
      </Card>
      <MemberCourseInquiryPanel />
    </div>
  );
}

function hasBirthdayWithin(birthday: string | null | undefined, days: number) {
  if (!birthday) return false;
  const source = new Date(`${birthday}T00:00:00`);
  if (Number.isNaN(source.getTime())) return false;
  const now = new Date();
  const next = new Date(now.getFullYear(), source.getMonth(), source.getDate());
  if (next < new Date(now.getFullYear(), now.getMonth(), now.getDate()))
    next.setFullYear(next.getFullYear() + 1);
  return next.getTime() - now.getTime() <= days * 86400000;
}

function memberStatusLabel(status: string | null | undefined) {
  return (
    {
      active: "在籍",
      expiring: "即将到期",
      expired: "已到期",
      paused: "暂停",
      frozen: "冻结",
    }[status ?? ""] ?? "未购课"
  );
}

function genderLabel(gender: string | null | undefined) {
  return { male: "男", female: "女", undisclosed: "性别未录入" }[
    gender ?? ""
  ] ?? "性别未录入";
}
